import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	ClickAwayListener,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Tab,
	Tabs,
	Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { OKRButton } from '../Common/OKRButton';
import AlertDialog from '../Common/Dialog';
import { AddIconSvg } from '../../config/svg/CommonSvg';
import { getMonthDate } from '../../config/utils';
import { UpdatesTable } from './UpdatesTable';
import { ComingNextTable } from './ComingNextTable';
import { AddUpdateDrawer } from './AddUpdateDrawer';
import { AddRoadmapItemDrawer } from './AddRoadmapItemDrawer';
import { AddVersionDrawer } from './AddVersionDrawer';
import {
	RoadmapRow,
	TargetQuarterOption,
	UpdateRow,
	VersionOption,
	WhatsNewComingNextApiRow,
	WhatsNewEntity,
	WhatsNewTypeOption,
	WhatsNewUpdateApiRow,
} from './mockData';
import {
	deleteComingNextAPI,
	deleteWhatsNewAPI,
	getAllComingNextAPI,
	getAllVersionsAPI,
	getAllWhatsNewAPI,
	getTargetQuartersAPI,
	getWhatsNewByIdAPI,
	getWhatsNewTypesAPI,
} from '../../services/whatsNewRoadmapService';
import '../../styles/pages/whats-new-roadmap/whatsNewRoadmap.scss';

interface TabPanelProps {
	children?: React.ReactNode;
	value: number;
	index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
	<Box role='tabpanel' hidden={value !== index} id={`whats-new-roadmap-tabpanel-${index}`}>
		{value === index && children}
	</Box>
);

export const WhatsNewRoadmap: React.FC<any> = () => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [tabSelected, setTabSelected] = useState(0);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [addVersionDrawerOpen, setAddVersionDrawerOpen] = useState(false);
	const [whatsNewTypes, setWhatsNewTypes] = useState<WhatsNewTypeOption[]>([]);
	const [rawUpdates, setRawUpdates] = useState<WhatsNewUpdateApiRow[]>([]);
	const [updatesLoading, setUpdatesLoading] = useState(true);
	const [roadmapItems, setRoadmapItems] = useState<RoadmapRow[]>([]);
	const [rawComingNext, setRawComingNext] = useState<WhatsNewComingNextApiRow[]>([]);
	const [comingNextLoading, setComingNextLoading] = useState(true);
	const [targetQuarters, setTargetQuarters] = useState<TargetQuarterOption[]>([]);
	const [versions, setVersions] = useState<VersionOption[]>([]);
	const [deleteRow, setDeleteRow] = useState<UpdateRow | null>(null);
	const [editEntity, setEditEntity] = useState<WhatsNewEntity | null>(null);
	const [deleteComingNextRow, setDeleteComingNextRow] = useState<RoadmapRow | null>(null);
	const [editComingNextEntity, setEditComingNextEntity] = useState<WhatsNewComingNextApiRow | null>(null);

	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setTabSelected(newValue);
	};

	const fetchWhatsNewData = () => {
		setUpdatesLoading(true);
		setComingNextLoading(true);
		return Promise.all([
			getWhatsNewTypesAPI(),
			getAllWhatsNewAPI(),
			getAllComingNextAPI(),
			getTargetQuartersAPI(),
			getAllVersionsAPI(),
		])
			.then(([typesResponse, updatesResponse, comingNextResponse, targetQuartersResponse, versionsResponse]) => {
				if (typesResponse?.data?.isSuccess) {
					setWhatsNewTypes(typesResponse.data.entityList || []);
				}
				if (updatesResponse?.data?.isSuccess) {
					setRawUpdates(updatesResponse.data.entityList || []);
				}
				if (targetQuartersResponse?.data?.isSuccess) {
					setTargetQuarters(targetQuartersResponse.data.entityList || []);
				}
				if (versionsResponse?.data?.isSuccess) {
					setVersions(versionsResponse.data.entityList || []);
				}
				if (comingNextResponse?.data?.isSuccess) {
					const comingNextEntities: WhatsNewComingNextApiRow[] = comingNextResponse.data.entityList || [];
					setRawComingNext(comingNextEntities);
					const rows: RoadmapRow[] = comingNextEntities.map((item) => ({
						id: item.oneGuruComingNextId,
						title: item.title,
						status: item.isPublished ? 'Published' : 'Planned',
						target: item.targetQuarterName,
						impacts: item.impactLabel,
					}));
					setRoadmapItems(rows);
				}
			})
			.catch((error) => {
				console.error("Failed to load What's New updates", error);
			})
			.finally(() => {
				setUpdatesLoading(false);
				setComingNextLoading(false);
			});
	};

	useEffect(() => {
		fetchWhatsNewData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updates = useMemo<UpdateRow[]>(() => {
		const typeNameById = new Map(whatsNewTypes.map((type) => [type.whatsNewTypeId, type.typeName]));
		return rawUpdates.map((update) => ({
			id: update.whatsNewId,
			title: update.title,
			typeId: update.whatsNewType,
			typeName: typeNameById.get(update.whatsNewType) || '',
			impacts: update.impactLabel,
			version: update.versionName,
			released: update.releaseDate ? getMonthDate(update.releaseDate) : '—',
			screensCount: update.screenshotCount,
		}));
	}, [rawUpdates, whatsNewTypes]);

	const handleDeleteClick = (row: UpdateRow) => {
		setDeleteRow(row);
	};

	const handleEditClick = async (row: UpdateRow) => {
		try {
			const response: any = await getWhatsNewByIdAPI(row.id);
			if (response?.data?.isSuccess) {
				setEditEntity(response.data.entity);
				setDrawerOpen(true);
			} else {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		} catch (error) {
			enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
		}
	};

	const handleDrawerClose = () => {
		setDrawerOpen(false);
		setEditEntity(null);
	};

	const handleAddMenuClose = () => {
		setAddMenuAnchorEl(null);
	};

	const handleAddButtonClick = (event: React.MouseEvent<HTMLElement>) => {
		if (tabSelected === 0) {
			setAddMenuAnchorEl((prev) => (prev ? null : event.currentTarget));
			return;
		}
		setEditComingNextEntity(null);
		setDrawerOpen(true);
	};

	const handleAddUpdateMenuClick = () => {
		handleAddMenuClose();
		setEditEntity(null);
		setDrawerOpen(true);
	};

	const handleAddVersionMenuClick = () => {
		handleAddMenuClose();
		setAddVersionDrawerOpen(true);
	};

	const handleAddVersionDrawerClose = () => {
		setAddVersionDrawerOpen(false);
	};

	const handleDeleteDialogAction = async (event: any, type: number) => {
		event?.preventDefault?.();
		if (type === 1 && deleteRow) {
			try {
				const response: any = await deleteWhatsNewAPI(deleteRow.id);
				if (response?.data?.isSuccess) {
					enqueueSnackbar(response.data.messageList?.Result || t('deletedMessage', { message: deleteRow.title }), {
						variant: 'success',
					});
					fetchWhatsNewData();
				} else {
					enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
				}
			} catch (error) {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		}
		setDeleteRow(null);
	};

	const handleComingNextEditClick = (row: RoadmapRow) => {
		const entity = rawComingNext.find((item) => item.oneGuruComingNextId === row.id) || null;
		setEditComingNextEntity(entity);
		setDrawerOpen(true);
	};

	const handleComingNextDeleteClick = (row: RoadmapRow) => {
		setDeleteComingNextRow(row);
	};

	const handleComingNextDrawerClose = () => {
		setDrawerOpen(false);
		setEditComingNextEntity(null);
	};

	const handleComingNextDeleteDialogAction = async (event: any, type: number) => {
		event?.preventDefault?.();
		if (type === 1 && deleteComingNextRow) {
			try {
				const response: any = await deleteComingNextAPI(deleteComingNextRow.id);
				if (response?.data?.isSuccess) {
					enqueueSnackbar(
						response.data.messageList?.Result || t('deletedMessage', { message: deleteComingNextRow.title }),
						{ variant: 'success' }
					);
					fetchWhatsNewData();
				} else {
					enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
				}
			} catch (error) {
				enqueueSnackbar(t('someErrorOccurred'), { variant: 'error' });
			}
		}
		setDeleteComingNextRow(null);
	};

	useEffect(() => {
		const scrollTabsBar = () => {
			const tabsBar = document.getElementById('whats-new-roadmap-subheader');
			if (window.pageYOffset >= 80) {
				tabsBar?.classList.add('sticky-header');
			}
			if (window.pageYOffset < 62) {
				tabsBar?.classList.remove('sticky-header');
			}
		};
		window.addEventListener('scroll', scrollTabsBar);
		return () => {
			window.removeEventListener('scroll', scrollTabsBar);
		};
	}, []);

	return (
		<Box className='whats-new-roadmap-wrap'>
			<Box className='whats-new-roadmap-header'>
				<Box>
					<Typography variant='h2' className='page-title'>
						{t('whatsNewRoadmapTitle')}
					</Typography>
				</Box>
				<OKRButton
					id='add-update-btn'
					className='btn-primary'
					icon={<AddIconSvg />}
					text={tabSelected === 0 ? t('addUpdateTitle') : t('addRoadmapItemTitle')}
					handleClick={handleAddButtonClick}
				/>
				{tabSelected === 0 && (
					<Popper
						className='whats-new-add-menu-popper'
						open={!!addMenuAnchorEl}
						anchorEl={addMenuAnchorEl}
						transition
						placement='bottom-end'
					>
						{({ TransitionProps }) => (
							<Grow {...TransitionProps}>
								<Paper>
									<ClickAwayListener onClickAway={handleAddMenuClose}>
										<MenuList>
											<MenuItem id='add-update-menu-item' onClick={handleAddUpdateMenuClick}>
												{t('addUpdateTitle')}
											</MenuItem>
											<MenuItem id='add-version-menu-item' onClick={handleAddVersionMenuClick}>
												{t('addVersionTitle')}
											</MenuItem>
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Grow>
						)}
					</Popper>
				)}
			</Box>

			<Box className='whats-new-roadmap-subheader' id='whats-new-roadmap-subheader'>
				<Tabs
					className='okr-oval-tabs whats-new-roadmap-tab'
					value={tabSelected}
					onChange={handleTabChange}
					aria-label={t('whatsNewRoadmapTitle')}
				>
					<Tab label={t('updatesLabel')} id='whats-new-roadmap-tab-0' />
					<Tab label={t('comingNextLabel')} id='whats-new-roadmap-tab-1' />
				</Tabs>
			</Box>

			<TabPanel value={tabSelected} index={0}>
				<UpdatesTable rows={updates} loading={updatesLoading} onEdit={handleEditClick} onDelete={handleDeleteClick} />
			</TabPanel>
			<TabPanel value={tabSelected} index={1}>
				<ComingNextTable
					rows={roadmapItems}
					loading={comingNextLoading}
					onEdit={handleComingNextEditClick}
					onDelete={handleComingNextDeleteClick}
				/>
			</TabPanel>

			{tabSelected === 0 ? (
				<AddUpdateDrawer
					open={drawerOpen}
					handleClose={handleDrawerClose}
					onSaved={fetchWhatsNewData}
					whatsNewTypes={whatsNewTypes}
					versions={versions}
					editEntity={editEntity}
				/>
			) : (
				<AddRoadmapItemDrawer
					open={drawerOpen}
					handleClose={handleComingNextDrawerClose}
					onSaved={fetchWhatsNewData}
					targetQuarters={targetQuarters}
					editEntity={editComingNextEntity}
				/>
			)}

			<AddVersionDrawer
				open={addVersionDrawerOpen}
				handleClose={handleAddVersionDrawerClose}
				onSaved={fetchWhatsNewData}
			/>

			{deleteRow && (
				<AlertDialog
					module='delete'
					message={t('deleteConfirmationForAlignment', { type: deleteRow.title })}
					handleCloseModal={handleDeleteDialogAction}
					modalOpen={!!deleteRow}
				/>
			)}

			{deleteComingNextRow && (
				<AlertDialog
					module='delete'
					message={t('deleteConfirmationForAlignment', { type: deleteComingNextRow.title })}
					handleCloseModal={handleComingNextDeleteDialogAction}
					modalOpen={!!deleteComingNextRow}
				/>
			)}
		</Box>
	);
};
